// frontend/src/components/AudienceApprovalStep.tsx
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Target, Loader, Send } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import AiLabel from './AiLabel';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { getSessionInfo, sendADKMessageStreaming, ADK_APP_NAME } from '../config/api';
import { PlatformType } from '../types';
import { isTriggerCommand } from '../lib/triggers';
import { CandidatePair, duplicateWarnings, formatShare } from '../lib/audienceProfile';

type AudienceType = 'affinity' | 'in_market' | 'life_event';

interface AudienceItem {
  id: string;
  name: string;
  type: AudienceType;
  why: string;
  /** False for insight-only audiences (life events) — no DV360 equivalent to target. */
  targetable?: boolean;
  /** Added from the stored taxonomy to meet the configured minimum, not
   *  suggested by the Audience Insights API — so it carries no reach. */
  from_mapping?: boolean;
  /**
   * Combined reach of the whole suggested set, identical on every card. Never
   * rendered per-card: four cards showing the same millions would read as four
   * separate reaches. The chat handler surfaces it once, in prose.
   */
  combined_reach?: number;
  /** Set by the backend; insight-only audiences arrive unselected. */
  selected?: boolean;
  /** Google's relevance tier for this brief; "" when it was not ranked. */
  relevance?: 'high' | 'medium' | '';
}

const AUDIENCE_TYPE_BADGES: Record<AudienceType, { label: string; bg: string; fg: string }> = {
  affinity: { label: 'Affinity', bg: '#dbeafe', fg: '#1d4ed8' },
  in_market: { label: 'In-Market', bg: '#d1fae5', fg: '#065f46' },
  // Insight-only: deliberately muted so it does not read as a targetable segment.
  life_event: { label: 'Insight only', bg: '#f3f4f6', fg: '#4b5563' },
};

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
}

interface AudienceApprovalStepProps {
  sessionId: string;
  platform: PlatformType;
  onAudienceApproved: () => void;
}

const audienceApprovalRegex = /```json:audience-approval\n([\s\S]*?)\n```/;

const AudienceApprovalStep: React.FC<AudienceApprovalStepProps> = ({
  sessionId,
  platform,
  onAudienceApproved,
}) => {
  const [running, setRunning] = useState(false);
  const [complete, setComplete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [audiences, setAudiences] = useState<AudienceItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [overlapPairs, setOverlapPairs] = useState<CandidatePair[]>([]);
  const [inputValue, setInputValue] = useState('');

  const outputRef = useRef<HTMLDivElement>(null);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!hasStarted.current && sessionId) {
      hasStarted.current = true;
      sendMessage(`RUN_AUDIENCE_APPROVAL Session ID: ${sessionId}`);
    }
  }, [sessionId]);

  const parseAudienceBlock = (content: string) => {
    const match = content.match(audienceApprovalRegex);
    if (!match) return null;
    try {
      const parsed = JSON.parse(match[1]);
      return (parsed.audiences || []) as AudienceItem[];
    } catch {
      return null;
    }
  };

  const parseOverlapPairs = (content: string): CandidatePair[] => {
    const match = content.match(audienceApprovalRegex);
    if (!match) return [];
    try {
      return (JSON.parse(match[1]).overlap || []) as CandidatePair[];
    } catch {
      return [];
    }
  };

  const stripAudienceBlock = (content: string) =>
    content.replace(audienceApprovalRegex, '').trim();

  const sendMessage = async (messageText: string) => {
    if (!messageText.trim() || !sessionId) return;
    const { userId } = getSessionInfo();

    // Same rule as every other panel: machine addressing is not a message.
    if (!isTriggerCommand(messageText)) {
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'user',
        content: messageText,
        timestamp: new Date(),
      }]);
    }

    setInputValue('');
    setRunning(true);

    try {
      const stream = await sendADKMessageStreaming(ADK_APP_NAME, userId, sessionId, messageText, platform);
      const reader = stream.getReader();
      const decoder = new TextDecoder();

      const assistantMsg: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
      };
      setMessages(prev => [...prev, assistantMsg]);

      let fullContent = '';
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });
        const lines = sseBuffer.split('\n');
        sseBuffer = lines.pop() ?? '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'content' && data.text) {
                fullContent += data.text;
                setMessages(prev =>
                  prev.map(m => m.id === assistantMsg.id ? { ...m, content: fullContent } : m)
                );
                // Parse audience block as it arrives
                const parsed = parseAudienceBlock(fullContent);
                if (parsed && parsed.length > 0 && audiences.length === 0) {
                  setAudiences(parsed);
                  setOverlapPairs(parseOverlapPairs(fullContent));
                  // Honour the backend's choice: insight-only audiences arrive with
                  // selected:false and the chat prose tells the user they are unselected.
                  setSelectedIds(new Set(parsed.filter(a => a.selected !== false).map(a => a.id)));
                }
              }
            } catch { /* ignore parse errors */ }
          }
        }
      }

      // Only complete if at least 1 audience was actually saved.
      // Advance straight away rather than rendering a second "Proceed"
      // button: confirming the audiences IS the decision, so a second click
      // asks the same question twice — and the handler's reply already says
      // the Plan Summary is next.
      const savedMatch = fullContent.match(/✅\s+(\d+)\s+audience/);
      if (savedMatch && parseInt(savedMatch[1], 10) > 0) {
        setComplete(true);
        onAudienceApproved();
      }

      setMessages(prev =>
        prev.map(m => m.id === assistantMsg.id ? { ...m, isStreaming: false } : m)
      );
      setRunning(false);
      setSaving(false);
    } catch (error) {
      console.error('AudienceApprovalStep error:', error);
      setRunning(false);
      setSaving(false);
    }
  };

  const toggleAudience = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    const selected = audiences.filter(a => selectedIds.has(a.id)).map(a => ({
      id: a.id, name: a.name, type: a.type,
    }));
    setSaving(true);
    const payload = JSON.stringify(selected);
    sendMessage(`SAVE_APPROVED_AUDIENCES\nSession ID: ${sessionId}\nAudiences: ${payload}`);
  };

  const handleSendInput = () => {
    if (inputValue.trim() && !running) {
      sendMessage(`RUN_AUDIENCE_APPROVAL\nSession ID: ${sessionId}\nUser Message: ${inputValue}`);
    }
  };

  return (
    <div className="config-content">
      <motion.div
        className="config-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="config-title">Step 3: Audience Approval</h2>
        <p className="config-subtitle">AI is selecting the best Google Ads audiences for your campaign brief</p>

        {/* Single unified container — fixed viewport height, internally manages scroll */}
        <div style={{
          marginTop: '24px',
          border: '2px solid rgba(33,33,33,0.1)',
          borderRadius: '12px',
          background: 'white',
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 300px)',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div className="agent-output-header">
            <Target size={18} />
            <span>Audience Suggestions</span>
            {running && <Loader className="spinner" size={16} />}
          </div>

          {/* Messages — compact, scrolls if long */}
          <div
            ref={outputRef}
            style={{
              overflowY: 'auto',
              maxHeight: audiences.length > 0 ? '160px' : undefined,
              flex: audiences.length > 0 ? 'none' : '1',
              padding: '16px 24px 12px',
              fontSize: '14px',
              lineHeight: '1.8',
              color: '#333',
              borderBottom: audiences.length > 0 && !complete ? '1px solid #e5e7eb' : 'none',
            }}
          >
            <AnimatePresence>
              {messages.map((message) => {
                const cleanContent = stripAudienceBlock(message.content);
                return (
                  <motion.div
                    key={message.id}
                    className={`message ${message.type}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="message-content">
                      {/* Audience Insights generates suggestions from the brief text. Shown
                          when any came from it; on the full CSV fallback (all from the
                          mapping) the notice above the panel already says an AI picked them. */}
                      {message.type === 'assistant'
                        && (parseAudienceBlock(message.content) || []).some((a) => !a.from_mapping) && (
                        <AiLabel text="Suggested from your brief by Google's AI · check before you approve" />
                      )}
                      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} className="markdown-content">
                        {cleanContent}
                      </ReactMarkdown>
                      {message.isStreaming && <span className="cursor">▊</span>}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {/* Audience cards + confirm — fills remaining height, scrollable */}
          {audiences.length > 0 && !complete && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px 8px', fontSize: '13px', fontWeight: 600, color: '#374151', flexShrink: 0 }}>
                Select audiences to test in scenario generation:
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }}>
                {(() => {
                  const warnings = duplicateWarnings(selectedIds, overlapPairs);
                  const nameOf = (id: string) => audiences.find(x => x.id === id)?.name || id;
                  return audiences.map(a => {
                  // Unknown types fall back to a neutral badge showing the raw value —
                  // never to Affinity, which would silently mislabel them.
                  const badge = AUDIENCE_TYPE_BADGES[a.type]
                    ?? { label: String(a.type), bg: '#f3f4f6', fg: '#4b5563' };
                  return (
                  <label
                    key={a.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      padding: '12px',
                      marginBottom: '8px',
                      borderRadius: '8px',
                      border: `2px solid ${selectedIds.has(a.id) ? '#8b5cf6' : '#e5e7eb'}`,
                      backgroundColor: selectedIds.has(a.id) ? '#faf5ff' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(a.id)}
                      onChange={() => toggleAudience(a.id)}
                      style={{ marginTop: '2px', accentColor: '#8b5cf6' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>{a.name}</span>
                        <span style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          padding: '2px 6px',
                          borderRadius: '4px',
                          backgroundColor: badge.bg,
                          color: badge.fg,
                          textTransform: 'uppercase',
                        }}>
                          {badge.label}
                        </span>
                        {a.relevance && (
                          <span style={{
                            fontSize: '11px', fontWeight: 600, padding: '2px 6px', borderRadius: '4px',
                            backgroundColor: a.relevance === 'high' ? '#ede9fe' : '#f3f4f6',
                            color: a.relevance === 'high' ? '#5b21b6' : '#4b5563',
                          }}>
                            {a.relevance === 'high' ? 'High relevance' : 'Medium relevance'}
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>{a.why}</p>
                      {a.from_mapping && (
                        <p style={{ fontSize: '11px', color: '#b45309', margin: '4px 0 0' }}>
                          Added to meet your minimum — matched from the stored
                          taxonomy, not suggested by the API, so it has no audience size.
                        </p>
                      )}
                      {/* Live with the selection: two audiences that are
                          mostly the same people add little reach together. */}
                      {warnings.has(a.id) && (
                        <p style={{
                          display: 'flex', gap: '6px', alignItems: 'flex-start',
                          fontSize: '11px', color: '#92400e', margin: '6px 0 0', lineHeight: 1.4,
                        }}>
                          <AlertTriangle size={12} style={{ color: '#b45309', flexShrink: 0, marginTop: '1px' }} />
                          <span>
                            Mostly the same people as{' '}
                            {warnings.get(a.id)!.map(w => `${nameOf(w.id)} (${formatShare(w.share)})`).join(', ')}.
                            {' '}Selecting both adds few new people.
                          </span>
                        </p>
                      )}
                    </div>
                  </label>
                  );
                  });
                })()}
              </div>
              {/* Confirm button pinned at bottom, always visible */}
              <div style={{ padding: '12px 16px 16px', flexShrink: 0, borderTop: '1px solid #e5e7eb' }}>
                <button
                  onClick={handleConfirm}
                  disabled={selectedIds.size === 0 || saving}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: selectedIds.size === 0 ? '#e5e7eb' : '#8b5cf6',
                    color: selectedIds.size === 0 ? '#9ca3af' : '#fff',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: selectedIds.size === 0 ? 'not-allowed' : 'pointer',
                  }}
                >
                  {saving ? 'Saving…' : `Confirm ${selectedIds.size} audience${selectedIds.size !== 1 ? 's' : ''} & Proceed to Summary`}
                </button>
              </div>
            </div>
          )}

          {/* Chat input when still loading audiences */}
          {!complete && audiences.length === 0 && (
            <div className="chat-input-container">
              <input
                type="text"
                className="chat-input"
                placeholder="Ask about the suggested audiences..."
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && !e.shiftKey && handleSendInput()}
                disabled={running}
              />
              <button className="send-button" onClick={handleSendInput} disabled={running || !inputValue.trim()}>
                <Send size={18} />
              </button>
            </div>
          )}
        </div>

      </motion.div>
    </div>
  );
};

export default AudienceApprovalStep;
